<template>
    <Filter ref="filter"
        :config="config"
        :has-actions="hasActions"
        :disabled-actions="disabledActions"
        v-model:search="search"
        v-model:limit="limit"
        @update:limit="page = 1"
        v-model:orders="orders"
        v-model:filters="filters"
        @select:action="newValue => $emit('select:action', newValue)"
        @action:reload="() => {this.page = 1, load()}"
        :selectedColumns="$refs?.THead?.selectedColumnNames"
    />

    <div class="table-responsive table-scrollable">
        <table class="table table-hover table-striped">
            <THead
                v-model:columns="columns"
                v-model:orders="orders"
                :config="config"
                @update:orders="load()"
                @action:reload="load()"
                :is-static="isStatic"
                :has-checkbox="hasCheckbox"
                :selected-all="selectedAll"
                @update:selected-all="newValue => $refs.body.selectAll(newValue)"
                ref="THead"
            />
            <TBody ref="body"
                :columns="columns"
                :items="items"
                :config="config"
                :has-checkbox="hasCheckbox"
                :selected="selected"
                :checkbox-key="checkboxKey"
                @update:selected="newValue => $emit('update:selected', newValue)"
                v-slot="{ column, item }"
            >
                <div :class="[column.type === 'number'? 'text-align-end' : '']">
                    <slot :name="`cell-${column.name}`" :column="column" :item="item">
                        <a :href="item[column.name]" v-if="column.type === 'link'">{{ item[column.name] }}</a>
                        <span v-else>{{ item[column.name] }}</span>
                    </slot>
                </div>
            </TBody>
        </table>
    </div>

    <Paginate v-model:page="page" @update:page="load()" :limit="limit" :total="total"/>
</template>

<script>
import Filter from "./FilterComponent.vue";
import THead from "./HeadComponent.vue";
import TBody from "./BodyComponent.vue";
import Paginate from "./PaginateComponent.vue";
import {httpClient} from "../../../services/HttpClient";

export default {
    name: 'DatatableComponent',
    props: {
        config: {
            type: Object,
            required: true
        },
        selected: {
            type: Array,
            default: () => []
        },
        checkboxKey: {
            type: String,
            default: 'id'
        },
        disabledActions: {
            type: Boolean,
            default: false
        },
    },
    emits: ['update:selected', 'select:action'],
    components: {Filter, THead, TBody, Paginate},
    data() {
        return {
            orders: this.config.orders,
            limit: this.config.limit,
            search: null,
            filters: this.config.filters.reduce((result, item) => {
                if (Array.isArray(item.value) && item.value.length > 0) {
                    result[item.name] = item.value
                }
                
                return result;
            }, {}),
            page: 1,
            total: this.config.limit,
            hasActions: this.config.actions.length > 0,
            hasCheckbox: this.config.hasCheckbox,
            isStatic: this.config.settings.isStatic,
            items: [],
            firstLoad: true,
            columns: this.config.columns.filter(column => column.default),
            additionalData: {}
        }
    },
    methods: {
        reload() {
            this.firstLoad = true;
            this.load();
        },
        getQueryParams() {
            return {
                columns: this.columns.map(column => {
                    return {
                        orderable: column.orderable,
                        searchable: column.searchable,
                        name: column.name,
                        additional: column.additional,
                    }
                }),
                orders: this.orders,
                filters: this.filters,
                search: {
                    value: this.search,
                    regex: false
                },
                ...this.additionalData
            }
        },
        async load(additionalData = null) {
            if (additionalData) {
                this.additionalData = additionalData
            }
            if(this.config.url) {
                await httpClient.get(this.config.url, {
                    ...this.getQueryParams(),
                    limit: this.limit,
                    page: this.page,
                    first_load: this.firstLoad
                }).then((response) => {
                    this.items = response.data.data;
                    this.total = response.data.meta.total;
                    if (this.firstLoad) {
                        this.firstLoad = false;

                        if (response.data.meta?.filters) {
                            Object.entries(response.data.meta.filters).forEach(([key, value]) => {
                                let filter = this.config.filters.find(filter => filter.name === key || filter.key === key)

                                if (filter) {
                                    filter.options = value
                                }
                            })
                        }

                        if(response.data.meta) {
                            this.$refs.filter?.setAdditionalData(response.data.meta)
                            this.$refs.THead?.setCustomColumns(response.data.meta)
                        }
                    }
                });
            }
        },
    },
    computed: {
        selectedAll() {
            if (this.items.length !== 0) {
                return this.items.length === this.selected.length
            }
            return false
        }
    }
}
</script>

<style scoped>
.table-scrollable {
    max-height: none;
}
.text-align-end {
    text-align: end;
}
</style>
